import React from "react";
import Card from "components/card";

interface BeckScore {
  name: string;
  description: string;
  raw_score: number;
}

interface BeckScores {
  total: BeckScore;
  cognitive_affective: BeckScore;
  somatic: BeckScore;
}

interface BeckScoreDisplayProps {
  scores: BeckScores | Record<string, any>;
  interpretations?: Record<string, any>;
}

const getScoreSeverity = (score: number): { text: string; color: string } => {
  if (score <= 13) {
    return {
      text: "Mínima",
      color: "text-green-600 bg-green-100 dark:bg-green-900/30",
    };
  } else if (score <= 19) {
    return {
      text: "Leve",
      color: "text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30",
    };
  } else if (score <= 28) {
    return {
      text: "Moderada",
      color: "text-orange-600 bg-orange-100 dark:bg-orange-900/30",
    };
  } else {
    return {
      text: "Grave",
      color: "text-red-600 bg-red-100 dark:bg-red-900/30",
    };
  }
};

const ScoreCard = ({
  score,
  title,
  description,
}: {
  score: BeckScore;
  title: string;
  description: string;
}) => {
  const severity = getScoreSeverity(score.raw_score);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-navy-700 dark:bg-navy-800">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-lg font-semibold text-navy-700 dark:text-white">
          {title}
        </h4>
        <span
          className={`rounded-full px-3 py-1 text-sm font-medium ${severity.color}`}
        >
          {score.raw_score} - {severity.text}
        </span>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400">{description}</p>
    </div>
  );
};

const BeckScoreDisplay: React.FC<BeckScoreDisplayProps> = ({ scores }) => {
  return (
    <div className="space-y-4">
      {/* Total Score */}
      <ScoreCard
        score={scores.total}
        title="Puntuación Total BDI-II"
        description={scores.total.description}
      />

      {/* Dimensions */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <ScoreCard
          score={scores.cognitive_affective}
          title="Dimensión Cognitivo-Afectiva"
          description={scores.cognitive_affective.description}
        />
        <ScoreCard
          score={scores.somatic}
          title="Dimensión Somática"
          description={scores.somatic.description}
        />
      </div>

      {/* Legend */}
      <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-navy-700 dark:bg-navy-900">
        <h4 className="mb-2 font-medium text-navy-700 dark:text-white">
          Interpretación de Puntuaciones
        </h4>
        <div className="grid grid-cols-2 gap-2 text-sm md:grid-cols-4">
          <div className="rounded bg-green-100 px-3 py-2 dark:bg-green-900/30">
            <span className="font-medium text-green-600">0-13: Mínima</span>
          </div>
          <div className="rounded bg-yellow-100 px-3 py-2 dark:bg-yellow-900/30">
            <span className="font-medium text-yellow-600">14-19: Leve</span>
          </div>
          <div className="rounded bg-orange-100 px-3 py-2 dark:bg-orange-900/30">
            <span className="font-medium text-orange-600">20-28: Moderada</span>
          </div>
          <div className="rounded bg-red-100 px-3 py-2 dark:bg-red-900/30">
            <span className="font-medium text-red-600">29-63: Grave</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BeckScoreDisplay;
